/**
 * Main App Component
 * Root component with mobile-first layout and page routing
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from './store/uiStore';
import { useAuthStore } from './store/authStore';
import { authApi } from './api/transactionApi';
// authApi is used in handleLogout
import { AppLayout } from './components/layout/AppLayout';
import { type Page } from './components/layout/BottomNav';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { FinancialBalancePage } from './pages/FinancialBalancePage';
import { BudgetPage } from './pages/BudgetPage';
import { ReceitasPage } from './pages/ReceitasPage';
import { ReportsPage } from './pages/ReportsPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';

function ToastContainer() {
  const { toasts, removeToast } = useUIStore();

  useEffect(() => {
    const timer = setInterval(() => {
      if (toasts.length > 0 && toasts[0]) {
        removeToast(toasts[0].id);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [toasts, removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 space-y-2 max-w-lg mx-auto">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-up ${
            toast.type === 'success'
              ? 'bg-green-900/90 text-green-100 border border-green-700'
              : toast.type === 'error'
                ? 'bg-red-900/90 text-red-100 border border-red-700'
                : 'bg-slate-800/90 text-slate-100 border border-slate-700'
          }`}
        >
          {toast.type === 'success' && (
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {toast.type === 'error' && (
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="flex-1">{toast.message}</span>
          <button
            onClick={() => removeToast(toast.id)}
            className="ml-2 hover:opacity-70 transition-opacity shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const { isAuthenticated, logout: storeLogout } = useAuthStore();
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isViewingSettings, setIsViewingSettings] = useState(false);

  /**
   * Handle successful login callback
   * Called after LoginPage successfully authenticates
   */
  const handleLogin = useCallback(() => {
    // Reset page state after login
    setCurrentPage('home');
    setIsViewingSettings(false);
  }, []);

  /**
   * Handle logout - complete session cleanup
   */
  const handleLogout = useCallback(() => {
    // Clear legacy storage
    authApi.logout();

    // Reset local state
    setCurrentPage('home');
    setIsViewingSettings(false);

    // Perform full logout (clears storage and redirects)
    storeLogout(false);
  }, [storeLogout]);

  const handleNavigate = (page: Page) => {
    setCurrentPage(page);
    setIsViewingSettings(false);
  };

  const handleNavigateToSettings = () => {
    setIsViewingSettings(true);
  };

  const handleBackFromSettings = () => {
    setIsViewingSettings(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Handler for navigating to balance page from dashboard
  const handleNavigateToBalance = () => {
    setCurrentPage('balance');
    setIsViewingSettings(false);
  };

  // Get page title based on current page
  const getPageTitle = () => {
    if (isViewingSettings) {
      return t('pages.settings.title');
    }
    switch (currentPage) {
      case 'home':
        return t('pages.dashboard.title');
      case 'balance':
        return t('pages.home.title');
      case 'budget':
        return t('pages.budget.title');
      case 'receitas':
        return t('pages.receitas.title');
      case 'reports':
        return t('pages.reports.title');
      case 'profile':
        return t('pages.profile.title');
      default:
        return t('pages.dashboard.title');
    }
  };

  // Render current page
  const renderPage = () => {
    // Show settings page if viewing settings
    if (isViewingSettings) {
      return <SettingsPage onBack={handleBackFromSettings} />;
    }

    switch (currentPage) {
      case 'home':
        return <DashboardPage onNavigateToBalance={handleNavigateToBalance} />;
      case 'balance':
        return <FinancialBalancePage />;
      case 'budget':
        return <BudgetPage />;
      case 'receitas':
        return <ReceitasPage />;
      case 'reports':
        return <ReportsPage />;
      case 'profile':
        return <ProfilePage onLogout={handleLogout} onNavigateToSettings={handleNavigateToSettings} />;
      default:
        return <DashboardPage onNavigateToBalance={handleNavigateToBalance} />;
    }
  };

  return (
    <AppLayout
      currentPage={currentPage}
      onNavigate={handleNavigate}
      title={getPageTitle()}
    >
      {renderPage()}
      <ToastContainer />
    </AppLayout>
  );
}

export default App;
